import * as UpstreamReforge from "@reforge-com/react";
import { isNotNil } from "@vendrinc/frontend-ts";
import * as React from "react";
import * as FeatureFlags from "~/domain/featureFlags";
import { useAnalytics } from "~/hooks/useAnalytics";
import { useTrackedExperiments } from "~/hooks/useTrackedExperiments";

export const useReforge = () => {
  const reforge = UpstreamReforge.useReforge();
  const { isEnabled: isEnabledUpstream, get: getUpstream } = reforge;

  const { has: hasTrackedExperiment, add: addTrackedExperiment } =
    useTrackedExperiments();

  const { track } = useAnalytics();

  const trackExperimentIfNecessary = React.useCallback(
    (key: FeatureFlags.FlagName, value: unknown) => {
      if (
        FeatureFlags.isKnownExperiment(key) &&
        isNotNil(value) &&
        !hasTrackedExperiment(key, value)
      ) {
        addTrackedExperiment(key, value);

        track({
          type: "$experiment_started",
          properties: {
            "Experiment name": key,
            "Variant name": value?.toString() ?? "not-set",
          },
        });
      }
    },
    [addTrackedExperiment, hasTrackedExperiment, track],
  );

  // TODO: Remove me after 7/16/2025
  const publicApiTestingOverride = (key: FeatureFlags.FlagName) => {
    return (key === "clerk-auth" || key === "seller-portal") &&
      typeof window !== "undefined"
      ? document.cookie
          .split("; ")
          .find((row) => row.startsWith(`clerk_auth_override=`))
          ?.split("=")[1] === "true"
      : false;
  };

  const isEnabled = React.useCallback(
    (key: FeatureFlags.FlagName) => {
      const value = isEnabledUpstream(key) || publicApiTestingOverride(key);
      trackExperimentIfNecessary(key, value);
      return value;
    },
    [isEnabledUpstream, trackExperimentIfNecessary],
  );

  const get = React.useCallback(
    <T extends FeatureFlags.FlagName>(
      key: T,
    ): FeatureFlags.Flags[T] | null | undefined => {
      const value = FeatureFlags.decodeFlag(key, getUpstream(key));
      trackExperimentIfNecessary(key, value);

      // TODO: Remove this once Clerk Auth is enabled site wide
      if (key === ("clerk-auth" as const) && typeof window !== "undefined") {
        const clerkAuthOverride = document.cookie
          .split("; ")
          .find((row) => row.startsWith(`clerk_auth_override=`))
          ?.split("=")[1];

        if (clerkAuthOverride) {
          return {
            "clerk-auth": clerkAuthOverride === "true",
          } as unknown as FeatureFlags.Flags[T];
        }

        return value;
      }

      return value;
    },
    [getUpstream, trackExperimentIfNecessary],
  );

  return {
    ...reforge,
    isEnabled,
    get,
  };
};
